import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
import { trackMetaEvent } from '../hooks/useMetaPixel';
import { useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { getPublisherParams } from '../utils/publisherParams';
import { degreeLevelOptions, areaStudyOptions, programOptions, getAreaFromProgram, isValidProgram } from '../data/filterOptions';
import { useScreenInit } from '../useScreenInit.js';
import { RequestInfoSplitCard } from '../components/RequestInfoSplitCard';
import { GraduationCapIcon, CompassIcon, BadgeCheckIcon, DollarSignIcon, ClockIcon, BriefcaseIcon, BookOpenIcon, ShieldCheckIcon } from 'lucide-react';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { getMetaCookies, resolveSub3 } from '../utils/metaCookies';
// Resolves a deep-linked ?mProgram=<id> into the program + its parent area
// of study so the program-selection step can be pre-filled and skipped.
// Returns null when the param is absent or not a known program id.
const resolveProgramFromSearch = (search: string): {
  program: number;
  areaOfStudy: number;
} | null => {
  const raw = new URLSearchParams(search).get('mProgram');
  if (!raw) return null;
  const program = Number(raw);
  if (!Number.isFinite(program) || !isValidProgram(program)) return null;
  return {
    program,
    areaOfStudy: getAreaFromProgram(program)
  };
};
// Deep-link prefill support. Each answer can arrive either as the form's
// own value (e.g. highestEducation=some-college) or as the numeric
// Media-Matchers API code (e.g. highestEducation=5), so partner links can
// reuse whichever vocabulary they already send.
const EDUCATION_VALUES = ['some-high-school', 'high-school', 'some-college', 'associates', 'bachelors', 'masters', 'doctorate'];
const EDUCATION_BY_API_CODE: Record<string, string> = {
  '1': 'high-school',
  '2': 'some-high-school',
  '5': 'some-college',
  '7': 'bachelors'
};
const START_TIMEFRAME_VALUES = ['immediately', '1-3-months', '3-6-months', '6-12-months', '1-year-plus', 'undecided'];
const START_TIMEFRAME_BY_API_CODE: Record<string, string> = {
  '1': 'immediately',
  '2': '1-3-months',
  '3': '3-6-months',
  '4': '6-12-months',
  '5': '1-year-plus',
  '6': 'undecided'
};
const MILITARY_VALUES = ['none', 'active-duty', 'veteran', 'guard', 'spouse', 'dependent', 'unknown'];
// Inbound military codes mirror the partner spec: 1 None, 2 Active Duty,
// 3 Veteran, 4 National Guard, 5 Spouse, 6 Dependent. "Unknown" has no
// code (it's sent blank), so a blank/unrecognized value leaves the
// question unanswered and the user is asked it.
const MILITARY_BY_API_CODE: Record<string, string> = {
  '1': 'none',
  '2': 'active-duty',
  '3': 'veteran',
  '4': 'guard',
  '5': 'spouse',
  '6': 'dependent'
};
const resolveChoice = (raw: string | null, allowed: string[], byApiCode: Record<string, string>): string => {
  if (!raw) return '';
  const value = raw.trim().toLowerCase();
  if (allowed.includes(value)) return value;
  return byApiCode[value] || '';
};
const resolvePrefillFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const prefill: {
    areaOfStudy?: number;
    highestEducation?: string;
    hsGraduationYear?: string;
    startDate?: string;
    militaryAffiliation?: string;
    zipCode?: string;
    streetAddress?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } = {};
  // Area of study — prefills the first step's dropdown (the program
  // dropdown still has to be chosen unless mProgram is also supplied).
  const areaRaw = params.get('areaStudy') || params.get('areaOfStudy');
  if (areaRaw) {
    const area = Number(areaRaw);
    if (Number.isFinite(area) && programOptions[area]) {
      prefill.areaOfStudy = area;
    }
  }
  const education = resolveChoice(params.get('highestEducation'), EDUCATION_VALUES, EDUCATION_BY_API_CODE);
  if (education) prefill.highestEducation = education;
  // Graduation year — a 4-digit year inside the dropdown's range, or the
  // literal "before-1965" option.
  const gradYearRaw = (params.get('gradYear') || '').trim().toLowerCase();
  if (gradYearRaw) {
    const currentYear = new Date().getFullYear();
    const year = Number(gradYearRaw);
    if (gradYearRaw === 'before-1965' || gradYearRaw === '1965') {
      // 1965 is the value posted for the "Before 1965" option.
      prefill.hsGraduationYear = 'before-1965';
    } else if (Number.isInteger(year) && year > currentYear - 60 && year <= currentYear) {
      prefill.hsGraduationYear = String(year);
    }
  }
  const startTimeframe = resolveChoice(params.get('startTimeframe'), START_TIMEFRAME_VALUES, START_TIMEFRAME_BY_API_CODE);
  if (startTimeframe) prefill.startDate = startTimeframe;
  const military = resolveChoice(params.get('military') || params.get('militaryAffiliation'), MILITARY_VALUES, MILITARY_BY_API_CODE);
  if (military) prefill.militaryAffiliation = military;
  const zip = (params.get('zipCode') || '').replace(/\D/g, '').slice(0, 5);
  if (zip.length === 5) prefill.zipCode = zip;
  const address = (params.get('address') || params.get('streetAddress') || '').trim();
  if (address) prefill.streetAddress = address;
  const firstName = params.get('firstName');
  if (firstName) prefill.firstName = firstName;
  const lastName = params.get('lastName');
  if (lastName) prefill.lastName = lastName;
  const email = params.get('email');
  if (email) prefill.email = email;
  return prefill;
};
// Value mapping helpers for Media Matchers API
const educationToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    'some-high-school': 2,
    'high-school': 1,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[formValue] || 9; // Unknown
};
const startTimeframeToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[formValue] || 6; // Unknown
};
// military: None=1, Active Duty=2, Veteran=3, National Guard=4, Spouse=5,
// Dependent=6. Per the partner spec "Unknown" is sent BLANK rather than as
// a code, so anything unmapped resolves to an empty value.
const militaryAffiliationToApiValue = (formValue: string): string => {
  const map: Record<string, string> = {
    none: '1',
    'active-duty': '2',
    veteran: '3',
    guard: '4',
    spouse: '5',
    dependent: '6'
  };
  return map[formValue] || '';
};
// Map full US state names (and a few common variants) to 2-letter
// USPS abbreviations. Used to normalize the value sent to Media-Matchers
// APIs which expect "CA" rather than "California".
const STATE_ABBREVIATIONS: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'puerto rico': 'PR'
};
const toStateAbbr = (value: string): string => {
  if (!value) return '';
  const trimmed = value.trim();
  // Already a 2-letter abbreviation
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const lookup = STATE_ABBREVIATIONS[trimmed.toLowerCase()];
  return lookup || trimmed;
};
// Sanitize TCPA disclaimer HTML coming back from the Media-Matchers API.
// The API sometimes embeds <a href="…"> links pointing to partner
// privacy policies / terms pages, and we want those to render as real
// clickable links (not literal "<a href=…>" text). We allow only a
// minimal whitelist of inline tags and force every <a> to open in a
// new tab with safe rel attributes.
const sanitizeTcpaHtml = (raw: string): string => {
  if (!raw) return '';
  // Use the browser's own parser to walk the DOM and strip anything
  // outside the whitelist. This is safer than regex-only sanitization
  // because it normalizes weird markup, broken tags, attribute quoting, etc.
  if (typeof document === 'undefined') return raw;
  const ALLOWED_TAGS = new Set(['A', 'BR', 'STRONG', 'B', 'EM', 'I', 'SPAN', 'P']);
  const container = document.createElement('div');
  container.innerHTML = raw;
  const walk = (node: Element) => {
    // Iterate over a static copy because we mutate during traversal.
    Array.from(node.children).forEach(child => {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Replace disallowed element with its text content.
        const text = document.createTextNode(child.textContent || '');
        child.replaceWith(text);
        return;
      }
      // Strip all attributes except href on <a> tags.
      Array.from(child.attributes).forEach(attr => {
        if (child.tagName === 'A' && attr.name === 'href') return;
        child.removeAttribute(attr.name);
      });
      // Force safe defaults on every link.
      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || '';
        // Block javascript:, data:, vbscript: URIs.
        if (/^\s*(javascript|data|vbscript):/i.test(href)) {
          child.removeAttribute('href');
        }
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
        // Tailwind-ish underline styling so the link is obvious inside
        // the dense disclaimer paragraph.
        child.setAttribute('class', 'underline text-[#e8594f] hover:text-[#d64a41]');
      }
      walk(child);
    });
  };
  walk(container);
  return container.innerHTML;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
interface FormData {
  degreeLevel: number;
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  militaryAffiliation: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
interface SchoolResult {
  name?: string;
  schoolName?: string;
  logoUrl?: string;
  logo?: string;
  programName?: string;
  mRecordID?: string;
  recordID?: string;
  id?: string;
  tcpaText?: string;
  tcpa?: string;
}
// Icons available to the supporting-content cards under the hero.
export const SUPPORT_NOTE_ICONS = {
  degree: GraduationCapIcon,
  compass: CompassIcon,
  license: BadgeCheckIcon,
  money: DollarSignIcon,
  clock: ClockIcon,
  briefcase: BriefcaseIcon,
  book: BookOpenIcon,
  shield: ShieldCheckIcon
} as const;
export type SupportNoteIcon = keyof typeof SUPPORT_NOTE_ICONS;
// Landing-page copy for a template. Every field is optional; anything not
// supplied falls back to the default FindOnlineDegrees messaging.
export interface RequestInformationContent {
  metaTitle?: string;
  metaDescription?: string;
  badge?: string;
  headline?: string;
  headlineAccent?: string;
  subhead?: string;
  stats?: {
    value: string;
    label: string;
  }[];
  features?: string[];
  formHeading?: string;
  formSubhead?: string;
  // Optional full-bleed photo behind the hero. 'dark' (the default) lays
  // light copy over a dark scrim; 'light' keeps dark copy over a bright
  // scrim, for airy photos.
  heroBackgroundImage?: string;
  heroOverlay?: 'dark' | 'light';
  // Optional reassurance section rendered in a band under the hero.
  supportNote?: {
    heading: string;
    body: string;
    points?: {
      title: string;
      body: string;
      // Optional icon key — see SUPPORT_NOTE_ICONS below.
      icon?: SupportNoteIcon;
    }[];
    footnote?: string;
  };
}
export interface RequestInformationPageProps {
  // Template variant id. 'default' powers /request-information; named
  // variants (e.g. 'v1') power the /request-information/<variant> template
  // routes so each one can diverge from the default flow over time.
  variant?: string;
  // Optional landing-copy overrides for audience-specific templates.
  content?: RequestInformationContent;
  // 'default' keeps the hero + separate step pages. 'split-card' asks every
  // question inside one persistent card beside the marketing copy.
  layout?: 'default' | 'split-card';
  // Area-of-study landing pages lock the area (it's implied by the page) and
  // ask for degree level + program instead.
  lockedAreaId?: number;
}
export function RequestInformationPage({
  variant = 'default',
  content,
  layout = 'default',
  lockedAreaId
}: RequestInformationPageProps = {}) {
  const location = useLocation();
  // Screens preview: seeds the initial step so each screen renders its stage.
  const screenInit = useScreenInit();
  // Named templates all share the streamlined v1 flow: no degree-level
  // question, no military question, and URL prefill support. Only the
  // original /request-information route keeps the full question set.
  const usesStreamlinedFlow = variant !== 'default';
  // Streamlined templates drop the degree-level question, so the
  // `degreeInterest` parameter is also omitted from every API call and
  // downstream redirect.
  // Area-of-study landings still ask for degree level (the area is fixed by
  // the page), so they keep degreeInterest in the payload.
  const includeDegreeInterest = !usesStreamlinedFlow || Boolean(lockedAreaId);
  // They don't ask about military affiliation either. The answer defaults
  // to "None" (API value 1) so it's still posted with the lead.
  const showMilitaryStep = !usesStreamlinedFlow;
  // Deep links like ?mProgram=100 pre-select the program (and its area of
  // study) on the first step, which is still shown so the user can confirm
  // or change it.
  const deepLinkedProgram = usesStreamlinedFlow ? resolveProgramFromSearch(location.search) : null;
  // Remaining answers can also arrive on the URL (area of study, education,
  // grad year, start timeframe, military, ZIP, and contact fields). Those
  // steps are pre-selected but still shown, so the user can review or
  // change them before continuing.
  const urlPrefill = usesStreamlinedFlow ? resolvePrefillFromSearch(location.search) : null;
  useMetaTags({
    title: content?.metaTitle || 'Find Your Perfect Program | FindOnlineDegrees',
    description: content?.metaDescription || 'Get matched with accredited online degree programs tailored to your goals.'
  });
  const initialFormData: FormData = {
    degreeLevel: 0,
    areaOfStudy: lockedAreaId || deepLinkedProgram?.areaOfStudy || urlPrefill?.areaOfStudy || 0,
    program: deepLinkedProgram?.program || 0,
    highestEducation: urlPrefill?.highestEducation || '',
    hsGraduationYear: urlPrefill?.hsGraduationYear || '',
    militaryAffiliation: urlPrefill?.militaryAffiliation || (showMilitaryStep ? '' : 'none'),
    startDate: urlPrefill?.startDate || '',
    streetAddress: urlPrefill?.streetAddress || '',
    city: '',
    state: '',
    zipCode: urlPrefill?.zipCode || '',
    firstName: urlPrefill?.firstName || '',
    lastName: urlPrefill?.lastName || '',
    email: urlPrefill?.email || '',
    phone: '',
    consent: false
  };
  // The program-selection page always shows first — even when mProgram
  // pre-selects the program — so the user confirms what they're asking
  // about. Later steps answered by the URL are skipped from there.
  const [step, setStep] = useState(screenInit.step ?? 0);
  // Split-card layout only: step 0 is asked as two screens — area of study
  // (stage 0) then the program within it (stage 1).
  const [stage, setStage] = useState(screenInit.stage ?? (deepLinkedProgram ? 1 : 0));
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState<SchoolResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentSchoolIndex, setCurrentSchoolIndex] = useState(0);
  const [isSubmittingSchool, setIsSubmittingSchool] = useState(false);
  const [schoolTcpaChecked, setSchoolTcpaChecked] = useState(false);
  const [schoolError, setSchoolError] = useState('');
  // Clicks Search API state — drives the "additional matches" step
  // that runs after the Leads carousel. Same school-card UI as step 7
  // but no TCPA checkbox; clicking the CTA opens the partner's
  // clickURL and advances to the next match.
  const [clicksResults, setClicksResults] = useState<SchoolResult[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  // TCPA Search/Post API state — drives the contact-step (step 6) consent
  // disclaimer. Disclaimer text comes from the SEARCH API response; the
  // POST API is pinged on final submit with mRecordID + contact info.
  interface TcpaSearchResult {
    tcpaRequestId?: string;
    mRecordID?: string;
    mSessionID?: string;
    mBrandName?: string;
    mBrandID?: string;
    tcpaDisclaimer?: string;
    imageURL?: string;
  }
  const [tcpaResult, setTcpaResult] = useState<TcpaSearchResult | null>(null);
  const [tcpaLoading, setTcpaLoading] = useState(false);
  const [tcpaError, setTcpaError] = useState('');
  // Stable mSessionID for the lifetime of this page-view. Initialized
  // ONCE from the URL (?mSessionID=…) if provided by the upstream
  // referrer, otherwise generated fresh. All four Media-Matchers API
  // calls (leads search/post, tcpa search/post) use the same value so
  // server-side they can be correlated to a single session.
  const mSessionIdRef = useRef<string>('');
  if (!mSessionIdRef.current) {
    const incoming = new URLSearchParams(location.search).get('mSessionID');
    mSessionIdRef.current = incoming && incoming.trim() ? incoming.trim() : `s-${generateUUID()}`;
  }
  // Stable lead identifier for analytics: the LeadiD token when the script
  // has fired, otherwise a generated id kept for the life of the session.
  const leadIdRef = useRef<string>('');
  const getLeadIdToken = (): string => {
    const input = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    if (input?.value) return input.value;
    if (!leadIdRef.current) leadIdRef.current = generateUUID();
    return leadIdRef.current;
  };
  // Pre-populate the form from URL query params when the user lands here
  // from a partner referrer (e.g. the bridge page) that has already
  // collected first name, last name, and email. We only set values that
  // are present in the URL and don't already have something in state.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const prefill: Partial<Record<keyof FormData, string>> = {};
    const firstName = urlParams.get('firstName');
    const lastName = urlParams.get('lastName');
    const email = urlParams.get('email');
    if (firstName) prefill.firstName = firstName;
    if (lastName) prefill.lastName = lastName;
    if (email) prefill.email = email;
    if (Object.keys(prefill).length > 0) {
      console.log('[Prefill] applying URL params:', prefill);
      setFormData(prev => ({
        ...prev,
        ...prefill
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchUserLocation();
  }, []);
  // If the user lands on the address step and the geo lookup never
  // populated city/state/zip (first attempt failed silently, or providers
  // were blocked at page load), try again so the address fields can be
  // pre-filled before the user has to type them.
  useEffect(() => {
    if (step !== 5) return;
    if (formData.zipCode && formData.city && formData.state) return;
    console.log('[Geo] re-attempting IP lookup on address step');
    fetchUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // Inject LeadID + TrustedForm tracking scripts on mount.
  // Both scripts write their values into hidden form fields rendered below
  // (name="leadid_token" and name="xxTrustedFormCertUrl").
  useEffect(() => {
    // LeadID callback — writes the token into the leadid_token input
    ;
    (window as any).myCallbackFunction = function (token: string) {
      const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
      if (field) field.setAttribute('value', token);
    };
    // LeadID campaign script
    const leadIdScript = document.createElement('script');
    leadIdScript.id = 'LeadiDscript_campaign';
    leadIdScript.type = 'text/javascript';
    leadIdScript.async = true;
    leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
    document.body.appendChild(leadIdScript);
    // TrustedForm script — writes cert URL into xxTrustedFormCertUrl input
    const tfScript = document.createElement('script');
    tfScript.type = 'text/javascript';
    tfScript.async = true;
    const provideReferrer = false;
    tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape(String(provideReferrer)) + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
    document.body.appendChild(tfScript);
    return () => {
      // Best-effort cleanup so React StrictMode / route changes don't double-inject
      try {
        leadIdScript.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  // IP → ZIP lookup with multi-provider fallback. ipapi.co is the first
  // choice but it rate-limits aggressively (1k/day per IP) and returns
  // opaque 429s that surface as `Failed to fetch` in the browser. We fall
  // through to ipwho.is and ipinfo.io if earlier providers fail.
  const fetchUserLocation = async () => {
    interface GeoResult {
      city: string;
      state: string;
      zip: string;
    }
    const providers: {
      name: string;
      fetch: () => Promise<GeoResult | null>;
    }[] = [{
      name: 'ipapi.co',
      fetch: async () => {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) throw new Error(`ipapi.co ${res.status}`);
        const data = await res.json();
        if (data?.error) throw new Error(`ipapi.co: ${data.reason}`);
        return {
          city: data.city || '',
          state: data.region_code || '',
          zip: data.postal || ''
        };
      }
    }, {
      name: 'ipwho.is',
      fetch: async () => {
        const res = await fetch('https://ipwho.is/');
        if (!res.ok) throw new Error(`ipwho.is ${res.status}`);
        const data = await res.json();
        if (data?.success === false) throw new Error(`ipwho.is: ${data.message}`);
        return {
          city: data.city || '',
          state: data.region_code || data.region || '',
          zip: data.postal || ''
        };
      }
    }, {
      name: 'ipinfo.io',
      fetch: async () => {
        const res = await fetch('https://ipinfo.io/json');
        if (!res.ok) throw new Error(`ipinfo.io ${res.status}`);
        const data = await res.json();
        return {
          city: data.city || '',
          state: data.region || '',
          zip: data.postal || ''
        };
      }
    }];
    for (const p of providers) {
      try {
        const result = await p.fetch();
        if (result && (result.zip || result.city || result.state)) {
          console.log(`[Geo] resolved via ${p.name}`, result);
          setFormData(prev => ({
            ...prev,
            // Don't overwrite anything the user has already typed
            city: prev.city || result.city,
            state: prev.state || result.state,
            zipCode: prev.zipCode || result.zip
          }));
          return;
        }
      } catch (err) {
        console.warn(`[Geo] ${p.name} failed:`, err);
      }
    }
    console.warn('[Geo] all IP geolocation providers failed');
  };
  const lookupZipCode = async (zip: string) => {
    if (zip.length !== 5) return;
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      const data = await response.json();
      if (data.places && data.places[0]) {
        updateField('city', data.places[0]['place name']);
        updateField('state', data.places[0]['state abbreviation']);
      }
    } catch (err) {
      console.log('Could not lookup zip code');
    }
  };
  const updateField = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  // JSONP helper — bypasses CORS by loading the API as a script tag.
  // The API must support a callback query param (most lead APIs do).
  const jsonpRequest = <T,>(url: string, timeoutMs = 8000): Promise<T> => {
    return new Promise((resolve, reject) => {
      const callbackName = `mmcb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      const script = document.createElement('script');
      let settled = false;
      const cleanup = () => {
        try {
          delete (window as any)[callbackName];
        } catch {
          ;
          (window as any)[callbackName] = undefined;
        }
        if (script.parentNode) script.parentNode.removeChild(script);
      };
      (window as any)[callbackName] = (data: T) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(data);
      };
      const sep = url.includes('?') ? '&' : '?';
      script.src = `${url}${sep}callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('JSONP request failed'));
      };
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('JSONP request timed out'));
      }, timeoutMs);
      (window as any)[callbackName] = (origCb => (data: T) => {
        clearTimeout(timer);
        origCb(data);
      })((window as any)[callbackName]);
      document.body.appendChild(script);
    });
  };
  // The Leads Search supports an optional `excludeBrand` parameter — we
  // pass the brand id returned by the TCPA Search here so the Leads
  // result set never contains the same brand the user just consented
  // to on step 6 (avoiding a duplicate offer on step 7).
  const callSearchAPI = async (excludeBrand: string = '') => {
    setSearchLoading(true);
    const urlParams = new URLSearchParams(location.search);
    // Read values populated by the LeadID + TrustedForm scripts.
    // Fall back to a generated UUID for leadId if the script hasn't fired yet.
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const trustedFormUrl = tfInput?.value || '';
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear === 'before-1965' ? '1965' : formData.hsGraduationYear,
      campusType: '1',
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      mediaType: '1',
      leadIdToken,
      trustedFormUrl,
      mSessionID: mSessionIdRef.current
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (excludeBrand) {
      searchParams.set('excludeBrand', excludeBrand);
    }
    if (!includeDegreeInterest) {
      searchParams.delete('degreeInterest');
    }
    const url = `https://leads.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[Leads Search API] PING:', url, excludeBrand ? `(excluding brand ${excludeBrand})` : '');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[Leads Search API] response:', data);
      const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
      // Drop "ghost" entries that don't have a usable brand name — these
      // would otherwise render as a generic "Partner School" card with
      // an "Online Degree Program" pill and a templated TCPA disclaimer.
      const validSchools = Array.isArray(schools) ? schools.filter((s: any) => s?.mBrandName || s?.brandName || s?.name || s?.schoolName) : [];
      console.log(`[Leads Search API] ${validSchools.length} valid of ${Array.isArray(schools) ? schools.length : 0} returned schools`);
      setSearchResults(validSchools);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Search API error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };
  // TCPA Search API — fires when user reaches the contact step. Result
  // populates the consent disclaimer text.
  const callTcpaSearchAPI = async () => {
    setTcpaLoading(true);
    setTcpaError('');
    setTcpaResult(null);
    const urlParams = new URLSearchParams(location.search);
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear === 'before-1965' ? '1965' : formData.hsGraduationYear,
      campusType: '1',
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      mediaType: '2',
      leadIdToken,
      mSessionID: mSessionIdRef.current
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (!includeDegreeInterest) {
      searchParams.delete('degreeInterest');
    }
    const url = `https://tcpa.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[TCPA Search API] PING (GET):', url);
    // Primary path: plain GET via fetch. If CORS blocks the response,
    // fall back to JSONP (the API must support ?callback=…). Both paths
    // are GET requests so the server-side handling is identical.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: TcpaSearchResult = await response.json();
      console.log('[TCPA Search API] response (fetch):', data);
      setTcpaResult(data);
      setTcpaLoading(false);
      return data.mBrandID || '';
    } catch (fetchErr) {
      clearTimeout(timeout);
      console.warn('[TCPA Search API] fetch failed, falling back to JSONP:', fetchErr);
      try {
        const data = await jsonpRequest<TcpaSearchResult>(url, 8000);
        console.log('[TCPA Search API] response (jsonp):', data);
        setTcpaResult(data);
        setTcpaLoading(false);
        return data.mBrandID || '';
      } catch (jsonpErr) {
        console.error('[TCPA Search API] both fetch and JSONP failed:', jsonpErr);
        setTcpaError('Unable to load consent terms. Please refresh and try again.');
        setTcpaLoading(false);
        return '';
      }
    }
  };
  // TCPA Post API — fired on final submit with the mRecordID from the
  // TCPA search response and the user's contact info.
  //
  // Method: GET. We send the request THREE ways for maximum reliability,
  // because the user navigates to step 7 immediately after — and some
  // browsers cancel in-flight requests when the originating DOM element
  // is garbage-collected or the page re-renders:
  //   1) fetch(url, { keepalive: true, mode: 'no-cors' }) — survives
  //      navigation/unload and bypasses CORS preflight.
  //   2) <img> pixel appended to <body> — keeps the element alive long
  //      enough for the network request to complete.
  //   3) JSONP <script> tag — guaranteed visible in Network as a script
  //      request; works even if (1) and (2) are blocked.
  const callTcpaPostAPI = (onConfirmed?: () => void) => {
    const recordID = tcpaResult?.mRecordID;
    if (!recordID) {
      console.warn('[TCPA Post API] skipped — no mRecordID');
      return;
    }
    // Only report the submission once, from whichever transport confirms
    // first (keepalive fetch, pixel, or JSONP).
    let confirmed = false;
    const confirm = () => {
      if (confirmed) return;
      confirmed = true;
      onConfirmed?.();
    };
    const urlParams = new URLSearchParams(location.search);
    const phoneDigits = formData.phone.replace(/\D/g, '');
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    const postParams = new URLSearchParams({
      mRecordID: recordID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phoneDigits,
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      mSessionID: mSessionIdRef.current
    });
    if (fbp) postParams.set('fbp', fbp);
    if (fbc) postParams.set('fbc', fbc);
    // Publisher identification — same mapping as the Search calls:
    // URL mPubGroupID, or mPubID + mPubCampaignID, else the default group.
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => postParams.set(k, v));
    const url = `https://tcpa.media-matchers.com/post?${postParams.toString()}`;
    console.log('[TCPA Post API] PING (GET):', url);
    // (1) keepalive fetch — survives page navigation
    try {
      fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        keepalive: true
      }).then(() => confirm()).catch(err => console.warn('[TCPA Post API] fetch error:', err));
    } catch (err) {
      console.warn('[TCPA Post API] fetch threw:', err);
    }
    // (2) Image pixel — attached to body so it isn't GC'd mid-request
    try {
      const pixel = new Image();
      pixel.style.display = 'none';
      pixel.onload = () => {
        console.log('[TCPA Post API] pixel loaded');
        confirm();
      };
      pixel.onerror = () => console.warn('[TCPA Post API] pixel onerror (request may still have hit the server)');
      pixel.src = url;
      document.body.appendChild(pixel);
    } catch (err) {
      console.warn('[TCPA Post API] pixel error:', err);
    }
    // (3) JSONP fallback — guaranteed visible network request
    try {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => {
        console.log('[TCPA Post API] jsonp loaded');
        confirm();
        script.parentNode?.removeChild(script);
      };
      script.onerror = () => {
        console.warn('[TCPA Post API] jsonp onerror (request still went out)');
        script.parentNode?.removeChild(script);
      };
      document.body.appendChild(script);
    } catch (err) {
      console.warn('[TCPA Post API] jsonp error:', err);
    }
  };
  // Clicks Search API — fires after the user enters the Leads carousel
  // so results are ready by the time they finish. Returns partner
  // schools with a `clickURL` field; the user is redirected to that URL
  // when they click through. Same form payload as the Leads search.
  // The Clicks Search also accepts excludeBrand so the post-Leads
  // "additional matches" carousel never re-offers the same brand the
  // user already saw on step 6 (TCPA consent).
  const callClicksSearchAPI = async (excludeBrand: string = '') => {
    setClicksLoading(true);
    setClicksResults([]);
    const urlParams = new URLSearchParams(location.search);
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear === 'before-1965' ? '1965' : formData.hsGraduationYear,
      campusType: '1',
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      mediaType: '2',
      leadIdToken,
      mSessionID: mSessionIdRef.current
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (excludeBrand) {
      searchParams.set('excludeBrand', excludeBrand);
    }
    if (!includeDegreeInterest) {
      searchParams.delete('degreeInterest');
    }
    const url = `https://api.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[Clicks Search API] PING (GET):', url, excludeBrand ? `(excluding brand ${excludeBrand})` : '');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[Clicks Search API] response (fetch):', data);
      const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
    } catch (fetchErr) {
      clearTimeout(timeout);
      console.warn('[Clicks Search API] fetch failed, falling back to JSONP:', fetchErr);
      try {
        const data: any = await jsonpRequest(url, 8000);
        console.log('[Clicks Search API] response (jsonp):', data);
        const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
        setClicksResults(Array.isArray(schools) ? schools : []);
      } catch (jsonpErr) {
        console.error('[Clicks Search API] both fetch and JSONP failed:', jsonpErr);
        setClicksResults([]);
      }
    } finally {
      setClicksLoading(false);
    }
  };
  const callPostAPI = async (school: any) => {
    const recordID = school.mRecordID || school.recordID || school.mBrandID || school.brandID || school.id || '';
    if (!recordID) return;
    const urlParams = new URLSearchParams(location.search);
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const {
      fbp,
      fbc
    } = getMetaCookies();
    const postParams = new URLSearchParams({
      mRecordID: recordID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phoneDigits,
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      mSessionID: mSessionIdRef.current
    });
    if (fbp) postParams.set('fbp', fbp);
    if (fbc) postParams.set('fbc', fbc);
    // Publisher identification — same mapping as the Search calls:
    // URL mPubGroupID, or mPubID + mPubCampaignID, else the default group.
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => postParams.set(k, v));
    // Fire-and-forget via image pixel — bypasses CORS since we don't need
    // to read the response. Falls back silently on error.
    try {
      const pixel = new Image();
      pixel.src = `https://leads.media-matchers.com/post?${postParams.toString()}`;
    } catch (err) {
      console.error('Post API error:', err);
    }
  };
  // Advance past any of the single-question steps (1–4) that the URL
  // already answered, so the user only sees what's still missing.
  const advanceTo = (target: number): number => {
    if (!usesStreamlinedFlow) return target;
    const answered: Record<number, boolean> = {
      1: Boolean(formData.highestEducation),
      2: Boolean(formData.hsGraduationYear),
      3: Boolean(formData.startDate),
      4: Boolean(formData.militaryAffiliation)
    };
    let next = target;
    while (next >= 1 && next <= 4 && answered[next]) next += 1;
    return next;
  };
  const handleContinue = () => {
    if (step === 0) {
      if (includeDegreeInterest && !formData.degreeLevel) {
        setError('Please select a degree level');
        return;
      }
      if (!formData.areaOfStudy) {
        setError('Please select an area of study');
        return;
      }
      // Split-card layout asks for the area first, then the program.
      if (layout === 'split-card' && stage === 0) {
        setStage(1);
        setError('');
        return;
      }
      if (!formData.program) {
        setError('Please select a program');
        return;
      }
      setStep(advanceTo(1));
      return;
    }
    if (step === 1) {
      if (!formData.highestEducation) {
        setError('Please select your highest level of education');
        return;
      }
      setStep(advanceTo(2));
      return;
    }
    if (step === 2) {
      if (!formData.hsGraduationYear) {
        setError('Please select when you graduated high school');
        return;
      }
      setStep(advanceTo(3));
      return;
    }
    if (step === 3) {
      if (!formData.startDate) {
        setError('Please select when you would like to start classes');
        return;
      }
      setStep(advanceTo(4));
      return;
    }
    if (step === 4) {
      if (!formData.militaryAffiliation) {
        setError('Please select your military affiliation');
        return;
      }
      setStep(5);
      return;
    }
    if (step === 5) {
      if (!formData.streetAddress.trim()) {
        setError('Please enter your street address');
        return;
      }
      if (!formData.zipCode.trim()) {
        setError('Please enter your ZIP code');
        return;
      }
      // Trigger search APIs before moving to contact info.
      // - Leads search drives the per-school TCPA flow on step 7.
      // - TCPA search returns the disclaimer text rendered on step 6.
      startMatchSearches();
      setStep(6);
      return;
    }
    if (step === 6) {
      if (!formData.firstName.trim()) {
        setError('Please enter your first name');
        return;
      }
      if (!formData.lastName.trim()) {
        setError('Please enter your last name');
        return;
      }
      if (!formData.email.trim()) {
        setError('Please enter your email address');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!formData.phone.trim()) {
        setError('Please enter your phone number');
        return;
      }
      if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        setError('Please enter a valid 10-digit phone number');
        return;
      }
      if (!formData.consent) {
        setError('Please agree to be contacted');
        return;
      }
      handleSubmit();
      return;
    }
  };
  const handleBack = () => {
    setError('');
    if (layout === 'split-card') {
      // Program screen steps back to the area-of-study screen; the first
      // question after step 0 returns to the program screen.
      if (step === 0) {
        if (stage === 1) setStage(0);
        return;
      }
      if (step === 1) {
        setStage(1);
        setStep(0);
        return;
      }
    }
    if (step > 0 && step <= 6) {
      // Step 4 (military) isn't part of the v1 flow, so step over it.
      const previous = step - 1;
      setStep(!showMilitaryStep && previous === 4 ? 3 : previous);
      setError('');
    }
  };
  // Auto-advance when single-select steps get a value. The split-card
  // layout always waits for an explicit Continue click instead.
  const autoAdvance = layout === 'default';
  useEffect(() => {
    if (autoAdvance && step === 1 && formData.highestEducation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(2);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, step]);
  useEffect(() => {
    if (autoAdvance && step === 2 && formData.hsGraduationYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.hsGraduationYear, step]);
  useEffect(() => {
    if (autoAdvance && step === 3 && formData.startDate) {
      const timer = setTimeout(() => {
        setError('');
        setStep(showMilitaryStep ? 4 : 5);
      }, 400);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate, step]);
  useEffect(() => {
    if (autoAdvance && step === 4 && formData.militaryAffiliation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(5);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.militaryAffiliation, step]);
  // The match + consent lookups normally fire when the user leaves the
  // address step. Deep links can skip that step entirely, so this runs
  // them exactly once whenever the contact step is reached.
  const searchesStartedRef = useRef(false);
  const startMatchSearches = () => {
    if (searchesStartedRef.current) return;
    searchesStartedRef.current = true;
    callTcpaSearchAPI().then(excludeBrand => {
      callSearchAPI(excludeBrand);
    });
  };
  useEffect(() => {
    if (step !== 6) return;
    startMatchSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // A URL that supplies both address and ZIP answers the address step, so
  // v1 moves straight on to contact details.
  const addressFromUrlRef = useRef(Boolean(urlPrefill?.streetAddress && urlPrefill?.zipCode));
  useEffect(() => {
    if (!usesStreamlinedFlow || step !== 5 || !addressFromUrlRef.current) return;
    setStep(6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // Resolve city/state for a deep-linked ZIP so the payload is complete
  // even when the user never sees the address step.
  useEffect(() => {
    if (!urlPrefill?.zipCode) return;
    lookupZipCode(urlPrefill.zipCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) {
      return formData.phone;
    }
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };
  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    // The conversion is reported through GTM (dataLayer `form_submission`)
    // once the lead post is confirmed — no Meta Pixel event is fired here.
    setTimeout(() => {
      // Fire the TCPA Post API now that the user has checked the consent
      // box and provided their contact info. The dataLayer `form_submission`
      // event is pushed only once the lead post is confirmed.
      callTcpaPostAPI(() => {
        trackFormSubmit({
          formId: 'request-information',
          pageUrl: window.location.pathname,
          leadId: getLeadIdToken()
        });
      });
      // Kick off the Clicks Search API in the background so the
      // "additional matches" step (8) has results ready by the time the
      // user finishes the Leads carousel. Pass the TCPA brand id as
      // excludeBrand so the Clicks carousel never re-offers the same
      // brand the user already saw on step 6.
      callClicksSearchAPI(tcpaResult?.mBrandID || '');
      // Move to step 7 (TCPA per school) instead of redirecting immediately
      setStep(7);
    }, 350);
  };
  const handleSchoolAgree = async () => {
    if (!schoolTcpaChecked) {
      setSchoolError('Please check the box to consent before continuing.');
      return;
    }
    const currentSchool = searchResults[currentSchoolIndex];
    // Meta Pixel: Search — the user clicked through a school in the carousel.
    // Only the school brand is sent. Program / area-of-study are deliberately
    // omitted: per Meta's Business Tools Terms §1.h, event parameters must not
    // reflect or imply sensitive categories (e.g. health or mental health),
    // and several study areas would do exactly that.
    trackMetaEvent('Search', {
      search_string: (currentSchool as any)?.mBrandName || (currentSchool as any)?.brandName || (currentSchool as any)?.name || ''
    });
    setIsSubmittingSchool(true);
    await callPostAPI(currentSchool);
    setIsSubmittingSchool(false);
    advanceToNextSchool();
  };
  const handleSchoolSkip = () => {
    advanceToNextSchool();
  };
  const advanceToNextSchool = () => {
    setSchoolTcpaChecked(false);
    setSchoolError('');
    if (currentSchoolIndex < searchResults.length - 1) {
      setCurrentSchoolIndex(currentSchoolIndex + 1);
    } else {
      // Leads carousel done → advance to the Clicks "additional matches"
      // step. If no Clicks results are available we'll redirect home
      // from inside the step 8 render block.
      setStep(8);
    }
  };
  // Query string used when the flow finishes and the user is sent back to
  // the home/listings page. Variants without the degree-level question
  // (v1) forward only the selected program.
  const buildExitParams = () => {
    const params = new URLSearchParams({
      mProgram: formData.program.toString()
    });
    if (includeDegreeInterest) {
      params.set('degreeInterest', formData.degreeLevel.toString());
    }
    return params;
  };
  // Step 8 — Clicks "additional matches" handler. Opens the partner's
  // clickURL in a new tab and advances to the next school. When the
  // list is exhausted we send the user back to the home/listings page.
  const handleClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    // Meta Pixel: Search — the user clicked through a school in the
    // "additional matches" carousel.
    // Only the school brand is sent — see the note in handleSchoolAgree.
    trackMetaEvent('Search', {
      search_string: school?.mBrandName || school?.brandName || school?.name || ''
    });
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[Clicks] failed to open clickURL:', err);
      }
    } else {
      console.warn('[Clicks] no clickURL found on school:', school);
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      window.location.href = `/?${buildExitParams().toString()}`;
    }
  };
  const handleClicksSkip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      window.location.href = `/?${buildExitParams().toString()}`;
    }
  };
  const availablePrograms = formData.areaOfStudy ? programOptions[formData.areaOfStudy] || [] : [];
  // Hero copy flips to light type only over a dark scrim.
  const hasHeroImage = Boolean(content?.heroBackgroundImage) && content?.heroOverlay !== 'light';
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  // Step 7: TCPA per school
  if (step === 7) {
    // If search is still loading, show spinner
    if (searchLoading) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1981" data-mp="29skunhfg6tew">
          <div className="text-center" data-id="element-1982" data-mp="251b84pp74678">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-1983" data-mp="2509se7x8ivj6"></div>
            <p className="text-xl text-gray-700 font-semibold" data-id="element-1984" data-mp="1ha5t2cr2xhxz">
              Finding your school matches...
            </p>
          </div>
        </div>;
    }
    // If Leads returned no usable schools, skip the Leads carousel
    // entirely and jump directly to the Clicks carousel (step 8). The
    // Clicks Search has already been fired in the background by
    // handleSubmit, so it will either be ready or still loading by the
    // time step 8 renders.
    if (!searchResults || searchResults.length === 0) {
      console.log('[Step 7] no valid Leads schools — skipping to Clicks carousel (step 8)');
      setStep(8);
      return null;
    }
    const currentSchool: any = searchResults[currentSchoolIndex];
    const schoolName = currentSchool.mBrandName || currentSchool.brandName || currentSchool.name || currentSchool.schoolName || 'Partner School';
    const schoolLogo = currentSchool.imageURL || currentSchool.imageUrl || currentSchool.logoUrl || currentSchool.logo;
    const programName = currentSchool.exactProgramName || currentSchool.matchingProgramName || currentSchool.programName || 'Online Degree Program';
    const schoolHighlight = currentSchool.schoolHighlight || '';
    const aboutText = currentSchool.aboutBrand || currentSchool.aboutCollege || currentSchool.shortDescription || '';
    const richHtml = currentSchool.bodyContent || '';
    const tcpaText = currentSchool.tcpaDisclaimer || currentSchool.tcpaText || currentSchool.tcpa || currentSchool.disclosure || `By checking the box and clicking "I Agree & Continue," you consent to be contacted by ${schoolName} and its representatives regarding educational opportunities via email, phone, or text message using automated technology. Message and data rates may apply. Your consent is not required as a condition of enrollment.`;
    return <div className="min-h-screen bg-gray-50" data-id="element-1985" data-mp="1fialv725eaq5">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-1986" data-mp="1aieh35lmmf9j">
          {/* Progress indicator */}
          <div className="text-center mb-8" data-id="element-1987" data-mp="39uqq62bdvesd">
            <p className="text-sm text-gray-600 font-semibold" data-id="element-1988" data-mp="39se87p5ml4fb">
              School {currentSchoolIndex + 1} of {searchResults.length}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-1989" data-mp="2dprxoykixj9u">
              <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
              width: `${(currentSchoolIndex + 1) / searchResults.length * 100}%`
            }} data-id="element-1990" data-mp="2ipebb949l1j6" />
            </div>
          </div>

          {/* School card */}
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1991" data-mp="28x8hruyd6c1v">
            {schoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-1992" data-mp="1z15fmp3iidex">
                <span aria-hidden="true" data-id="element-1993" data-mp="1q0fr86gkzpb8">✨</span>
                {schoolHighlight}
              </span>}
            <div className="text-center mb-8" data-id="element-1994" data-mp="3ffdqhmex6yaq">
              {schoolLogo && <img src={schoolLogo} alt={schoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-1995" data-mp="3fg6r05nacrkf" />}
              {aboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1996" data-mp="3kc47qd2gnzxq">
                  {aboutText}
                </p>}
              <div className="mt-4 inline-flex flex-col items-center" data-id="element-1997" data-mp="13stir7doxrng">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1" data-id="element-1998" data-mp="1igy5qnjs0pc1">
                  Your matching program
                </span>
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#e8594f]/10 text-[#e8594f] font-bold text-sm" data-id="element-1999" data-mp="1dowriw31sojd">
                  {programName}
                </span>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3" data-id="element-2000" data-mp="1mkw1co9w7i0c">
              {schoolName}
            </h2>

            {richHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
            __html: richHtml
          }} data-id="element-2001" data-mp="1whqzh6297ge3" />}

            {/* TCPA consent — required */}
            <label className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 cursor-pointer hover:bg-blue-100 transition-colors" data-id="element-2002" data-mp="1v804cp15bbsf">
              <input type="checkbox" checked={schoolTcpaChecked} onChange={e => {
              setSchoolTcpaChecked(e.target.checked);
              if (e.target.checked) setSchoolError('');
            }} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f] flex-shrink-0" data-id="element-2003" data-mp="1q8n9bh04eyer" />
              <span className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpaText)
            }} data-id="element-2004" data-mp="1c9e2v91pvinz" />
            </label>

            {schoolError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2005" data-mp="1m8mtbrxdif5l">
                <p className="text-red-600 text-sm" data-id="element-2006" data-mp="1h6nt2qwxjycv">{schoolError}</p>
              </div>}

            <div className="space-y-4" data-id="element-2007" data-mp="2xl5rz69togbq">
              <button onClick={handleSchoolAgree} disabled={isSubmittingSchool || !schoolTcpaChecked} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2008" data-mp="2xobk6udqzybo">
                {isSubmittingSchool ? 'Submitting...' : 'I Agree & Continue'}
              </button>
              <button onClick={handleSchoolSkip} disabled={isSubmittingSchool} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2009" data-mp="2r8bfbthlzcze">
                Skip This School
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Step 8: Additional matches via Clicks API. Same school-card design
  // as step 7 but with NO TCPA checkbox — clicking the CTA opens the
  // partner's clickURL in a new tab and advances to the next school.
  if (step === 8) {
    if (clicksLoading) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-2010" data-mp="ai4l0b4te090">
          <div className="text-center" data-id="element-2011" data-mp="kdbg1exu3upo">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-2012" data-mp="kca4zagtj87y"></div>
            <p className="text-xl text-gray-700 font-semibold" data-id="element-2013" data-mp="gcrjodl34v4l">
              Finding additional matches...
            </p>
          </div>
        </div>;
    }
    if (!clicksResults || clicksResults.length === 0) {
      window.location.href = `/?${buildExitParams().toString()}`;
      return null;
    }
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick.mBrandName || currentClick.brandName || currentClick.name || currentClick.schoolName || 'Partner School';
    const cSchoolLogo = currentClick.imageURL || currentClick.imageUrl || currentClick.logoUrl || currentClick.logo;
    // headContent is the marketing headline / program promo for this
    // school (e.g. "Earn a Degree in Psychology from Keiser®.").
    // It often differs from the school name and is the best signal of
    // what the user will see when they click through.
    const cHeadline = currentClick.headContent || currentClick.exactProgramName || currentClick.matchingProgramName || currentClick.programName || '';
    const cSchoolHighlight = currentClick.schoolHighlight || '';
    const cAboutText = currentClick.aboutBrand || currentClick.aboutCollege || currentClick.shortDescription || '';
    const cAccreditation = currentClick.accreditation || '';
    const cRichHtml = currentClick.bodyContent || '';
    const cClickURL = currentClick.clickURL || currentClick.clickUrl || currentClick.url || currentClick.link;
    return <div className="min-h-screen bg-gray-50" data-id="element-2014" data-mp="2qyjyn8eefe4v">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-2015" data-mp="2qwpc5pnhbdyo">
          {/* Progress indicator */}
          <div className="text-center mb-8" data-id="element-2016" data-mp="2vqjwmvawrwyt">
            <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-2017" data-mp="1y9mmzo4p0fl3">
              Additional Matches
            </p>
            <p className="text-sm text-gray-600 font-semibold" data-id="element-2018" data-mp="237en35fhr80d">
              School {clicksIndex + 1} of {clicksResults.length}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-2019" data-mp="8q4qzi27705v">
              <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
              width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
            }} data-id="element-2020" data-mp="3rjsbn2lgojj" />
            </div>
          </div>

          {/* School card */}
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-2021" data-mp="ri7b53e1sykv">
            {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-2022" data-mp="ngu5ataygvdy">
                <span aria-hidden="true" data-id="element-2023" data-mp="wilbs0dbl68v">✨</span>
                {cSchoolHighlight}
              </span>}
            <div className="text-center mb-8" data-id="element-2024" data-mp="3391je37sp89q">
              {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-2025" data-mp="2ya86abwf7ja3" />}
              {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-2026" data-mp="1dsp70wv5u2iu">
                  {cAboutText}
                </p>}
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-2027" data-mp="141uj7o29muva">
              {cSchoolName}
            </h2>

            {/* Marketing headline from the Clicks API (headContent). Shown
                only when it provides additional info beyond the school
                name itself. */}
            {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-2028" data-mp="zxmu3prwbcwj">
                {cHeadline}
              </p>}

            {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-2029" data-mp="q2l6iyd9qiw2">
                Accredited by {cAccreditation}
              </p>}

            {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
            __html: cRichHtml
          }} data-id="element-2030" data-mp="2kfuh6thjwafw" />}

            <div className="space-y-4" data-id="element-2031" data-mp="2dz1clbkvdzuz">
              <button onClick={handleClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2032" data-mp="2dxzs6ghys10l">
                Visit {cSchoolName} →
              </button>
              <button onClick={handleClicksSkip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-2033" data-mp="31r9lo5blv6od">
                Skip This School
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Split-card layout: the marketing copy stays put while every question
  // (steps 0–6) is asked inside one persistent card beside it. Steps 7–8
  // (the school carousels) are handled above and shared with all layouts.
  if (layout === 'split-card') {
    return <div data-template-variant={variant} data-id="element-2034" data-mp="127bswrh66ecu">
        {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
        <input type="hidden" name="leadid_token" value="" data-id="element-2035" data-mp="175n4p7r0yyxp" />
        <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-2036" data-mp="1b6p3jnh158z5" />
        <RequestInfoSplitCard badge={content?.badge || 'Trusted by 70,000+ Students'} headline={content?.headline || 'Find Your Perfect'} headlineAccent={content?.headlineAccent || 'Online Degree'} subhead={content?.subhead || 'Get matched with accredited programs tailored to your goals. Compare schools, explore financial aid, and start your next chapter today.'} features={content?.features || ['Accredited institutions only', 'Financial aid guidance', 'Personalized program matches', 'No cost, no obligation']} step={step} stage={stage} formData={formData} availablePrograms={availablePrograms} years={years} error={error} tcpaLoading={tcpaLoading} tcpaError={tcpaError} tcpaDisclaimerHtml={tcpaResult?.tcpaDisclaimer ? sanitizeTcpaHtml(tcpaResult.tcpaDisclaimer) : null} onFieldChange={(field, value) => updateField(field as keyof FormData, value)} onAreaSelect={area => {
        updateField('areaOfStudy', area);
        updateField('program', 0);
      }} onZipChange={zip => {
        updateField('zipCode', zip);
        if (zip.length === 5) lookupZipCode(zip);
      }} onPhoneChange={handlePhoneChange} onContinue={handleContinue} onBack={handleBack} data-id="element-2037" data-mp="1g61pm602uc0o" />
      </div>;
  }
  return <div className="min-h-screen bg-gray-50" data-template-variant={variant} data-id="element-2038" data-mp="10yzc86tl9usw">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-2039" data-mp="19ufdva0l5e91" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-2040" data-mp="152p352xqkoh3" />
      {/* Landing Page - Step 0 */}
      {step === 0 && <div data-id="element-2041" data-mp="hckwsnt5yame">
          {/* Hero block — the optional photo + scrim sit behind this section
              only, so any bands below keep their own background. */}
          <div className="relative" data-id="element-2042" data-mp="d9esvmtiwvj3">
          {content?.heroBackgroundImage && <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-2043" data-mp="36stnvka8kdk">
              <img src={content.heroBackgroundImage} alt="" className="w-full h-full object-cover" data-id="element-2044" data-mp="7zcffft4iltg" />
              {content.heroOverlay === 'light' ? <div className="absolute inset-0 bg-white/70 lg:bg-gradient-to-r lg:from-white/90 lg:via-white/75 lg:to-white/40" data-id="element-2045" data-mp="3pco9yf2olbcw" /> : <div className="absolute inset-0 bg-gray-900/75 lg:bg-gradient-to-r lg:from-gray-900/90 lg:via-gray-900/70 lg:to-gray-900/40" data-id="element-2046" data-mp="3ubj6ejtkmyfh" />}
            </div>}
          {/* Hero Section */}
          <div className={`relative max-w-7xl mx-auto px-4 py-6 md:py-16 lg:py-24 ${hasHeroImage ? 'text-white' : ''}`} data-id="element-2047" data-mp="qusslirqzh38">
            {/* Mobile-only compact heading — keeps the form above the fold
             on phones while still framing what the page is. */}
            <div className="lg:hidden text-center mb-5" data-id="element-2048" data-mp="2wpc0n6lj701j">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-3" data-id="element-2049" data-mp="31oydhexg6e9t">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2050" data-mp="1z863vmvuwsqp">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2051" data-mp="239hw80647s2l" />
                </svg>
                {content?.badge || 'Trusted by 70,000+ Students'}
              </div>
              <h1 className={`text-2xl sm:text-3xl font-bold leading-tight ${hasHeroImage ? 'text-white' : 'text-gray-900'}`} data-id="element-2052" data-mp="1fm9n175kfyva">
                {content?.headline || 'Find Your Perfect'}{' '}
                <span className={hasHeroImage ? 'text-[#ffb4ad]' : 'text-[#e8594f]'} data-id="element-2053" data-mp="1pi37dcpj0wnm">
                  {content?.headlineAccent || 'Online Degree'}
                </span>
              </h1>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-2054" data-mp="wtnxt5p5z6jo">
              {/* Left Column - Content (below the form on mobile) */}
              <div className="text-center lg:text-left order-2 lg:order-1" data-id="element-2055" data-mp="ua13lrg1qy62">
                <div className="hidden lg:inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-2056" data-mp="u7yagyjlkox4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-2057" data-mp="katp30xksxhd">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2058" data-mp="fda1vbiwhada" />
                  </svg>
                  {content?.badge || 'Trusted by 70,000+ Students'}
                </div>

                <h1 className={`hidden lg:block text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight ${hasHeroImage ? 'text-white' : 'text-gray-900'}`} data-id="element-2059" data-mp="1mexocjcjhg1">
                  {content?.headline || 'Find Your Perfect'}
                  <span className={`block ${hasHeroImage ? 'text-[#ffb4ad]' : 'text-[#e8594f]'}`} data-id="element-2060" data-mp="3nrscnl08740l">
                    {content?.headlineAccent || 'Online Degree'}
                  </span>
                </h1>

                <p className={`text-base md:text-xl mb-6 md:mb-8 leading-relaxed ${hasHeroImage ? 'text-gray-100' : 'text-gray-600'}`} data-id="element-2061" data-mp="397zc9atk5lk8">
                  {content?.subhead || 'Get matched with accredited programs tailored to your goals. Compare schools, explore financial aid, and start your journey today.'}
                </p>

                {/* Trust Signals */}
                

                {/* Features */}
                <div className="space-y-3 mb-8" data-id="element-2062" data-mp="q1tsjkw1l5j">
                  {(content?.features || ['Accredited institutions only', 'Financial aid guidance', 'Personalized program matches', 'No application fees']).map((feature, idx) => <div key={idx} className={`flex items-center gap-3 ${hasHeroImage ? 'text-gray-100' : 'text-gray-700'}`} data-id="element-2063" data-mp="3o2vsaa7lzd7v" data-mpi={idx}>
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-2064" data-mp="fmarvht1lsgd">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2065" data-mp="kfloyr81yuv3" />
                      </svg>
                      <span className="font-medium" style={{
                    textAlign: "left"
                  }} data-id="element-2066" data-mp="ppg0g1dvbjob">{feature}</span>
                    </div>)}
                </div>

              </div>

              {/* Right Column - Form (first on mobile, above the fold) */}
              <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 md:p-10 border border-gray-100 order-1 lg:order-2" data-id="element-2067" data-mp="at8aekfs3o14">
                <div className="text-center mb-5 md:mb-8" data-id="element-2068" data-mp="6tzg9egtto51">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2" data-id="element-2069" data-mp="kspdqv7k5ptx">
                    {content?.formHeading || 'Get Started in 60 Seconds'}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600" data-id="element-2070" data-mp="1w6td6gwx066d">
                    {content?.formSubhead || 'Tell us about your educational goals'}
                  </p>
                </div>

                <div className="space-y-4 md:space-y-5" data-id="element-2071" data-mp="rnyn51gf80j">
                  {includeDegreeInterest && <div data-id="element-2072" data-mp="5pz9nj3hjokc">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2073" data-mp="5radtk0ueybz">
                        Degree Level <span className="text-red-500" data-id="element-2074" data-mp="ao94ttp7mp5y">*</span>
                      </label>
                      <select value={formData.degreeLevel} onChange={e => updateField('degreeLevel', Number(e.target.value))} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2075" data-mp="er5shytlxbgr">
                        <option value="" data-id="element-2076" data-mp="yfel29z2bjgs">-- Please Select --</option>
                        {degreeLevelOptions.map(option => <option key={option.value} value={option.value} data-id="element-2077" data-mp="12h04yfa0ky0h">
                            {option.label}
                          </option>)}
                      </select>
                    </div>}

                  {!lockedAreaId && <div data-id="element-2078" data-mp="2ryexayifolsi">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2079" data-mp="2rywym9jydq7a">
                      Area of Study <span className="text-red-500" data-id="element-2080" data-mp="2n0n666pm53pd">*</span>
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                    updateField('areaOfStudy', Number(e.target.value));
                    updateField('program', 0);
                  }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2081" data-mp="2ix7b3et6zbes">
                      <option value="" data-id="element-2082" data-mp="2wxi2ciyeys0h">-- Please Select --</option>
                      {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-2083" data-mp="3uccd0gli15xc">
                          {option.label}
                        </option>)}
                    </select>
                  </div>}

                  <div data-id="element-2084" data-mp="cbyprdl8tdvw">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2085" data-mp="ccs16rv5crkd">
                      Program <span className="text-red-500" data-id="element-2086" data-mp="hatib790igt7">*</span>
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', Number(e.target.value))} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2087" data-mp="3ivd37if952b">
                      <option value="" data-id="element-2088" data-mp="3krif41ismc5o">
                        {formData.areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                      </option>
                      {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-2089" data-mp="1j0yn6v6dnq07">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2090" data-mp="2zi5ojvq9l9y1">
                      <p className="text-red-600 text-sm" data-id="element-2091" data-mp="3d8smhlo7avt2">{error}</p>
                    </div>}

                  <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-2092" data-mp="38haq7ax4hjef">
                    Continue to Next Step →
                  </button>

                  <p className="text-center text-xs text-gray-500 mt-4" data-id="element-2093" data-mp="3i9yhxr98wx6a">
                    🔒 Your information is secure and confidential
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Audience reassurance band (template-specific) */}
          {content?.supportNote && <section className="bg-white border-t border-gray-100 py-14" data-id="element-2094" data-mp="1wv2isg0zu9jy">
              <div className="max-w-5xl mx-auto px-4" data-id="element-2095" data-mp="1byl9oat7w3mf">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center" data-id="element-2096" data-mp="1by3b37j31dbc">
                  {content.supportNote.heading}
                </h2>
                <p className="text-base text-gray-600 leading-relaxed max-w-3xl mx-auto text-center" data-id="element-2097" data-mp="1kqn3hq8s6lnq">
                  {content.supportNote.body}
                </p>
                {content.supportNote.points && <div className="grid md:grid-cols-3 gap-6 mt-10" data-id="element-2098" data-mp="t0ine3s20ksb">
                    {content.supportNote.points.map(point => {
              const PointIcon = point.icon ? SUPPORT_NOTE_ICONS[point.icon] : null;
              return <div key={point.title} className="border border-gray-200 rounded-xl p-6 text-center" data-id="element-2099" data-mp="11znlxa3m8e4h">
                        {PointIcon && <span className="mx-auto mb-4 flex w-11 h-11 items-center justify-center rounded-lg bg-[#e8594f]/10" data-id="element-2100" data-mp="170tg7ccxc9g4">
                            <PointIcon className="w-5 h-5 text-[#e8594f]" aria-hidden="true" data-id="element-2101" data-mp="2ieotefk9q84p" />
                          </span>}
                        <h3 className="text-base font-bold text-gray-900 mb-2" data-id="element-2102" data-mp="6rlb54vpxfdf">
                          {point.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2103" data-mp="1ubpm0bg9nuv">
                          {point.body}
                        </p>
                      </div>;
            })}
                  </div>}
                {content.supportNote.footnote && <p className="text-xs text-gray-500 leading-relaxed max-w-3xl mx-auto text-center mt-8" data-id="element-2104" data-mp="3k6rh8c8b2c0r">
                    {content.supportNote.footnote}
                  </p>}
              </div>
            </section>}
        </div>}

      {/* Form Steps (1-6) */}
      {step > 0 && step <= 6 && <div className="max-w-4xl mx-auto px-4 py-6 md:py-12" data-id="element-2105" data-mp="3597e7auyyqx8">
          {/* Progress Bar */}
          <div className="mb-6 md:mb-12" data-id="element-2106" data-mp="18gfd0v7gpkud">
            <div className="flex items-center justify-between mb-3 md:mb-4 max-w-2xl mx-auto gap-2" data-id="element-2107" data-mp="18fe2qsy9t2dp">
              <div className="flex-1 text-center" data-id="element-2108" data-mp="13i420h5fl4ak">
                <div className={`text-[11px] sm:text-sm font-semibold mb-2 transition-colors ${step >= 1 && step < 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-2109" data-mp="lfvxav7l46t5">
                  ABOUT YOU
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-2110" data-mp="bre0ep2fejus">
                <div className={`text-[11px] sm:text-sm font-semibold mb-2 transition-colors ${step >= 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-2111" data-mp="bnzvpa6ahyk6">
                  CONTACT INFO
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-2112" data-mp="zg6ojzct3neu">
                <div className={`text-[11px] sm:text-sm font-semibold mb-2 transition-colors ${step > 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-2113" data-mp="zggexo1rjwl2">
                  SCHOOL MATCHES
                </div>
              </div>
            </div>

            {/* Progress Bar Track */}
            <div className="relative max-w-2xl mx-auto" data-id="element-2114" data-mp="3f849xdx7vf18">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2115" data-mp="3a7ydzljwz6dc">
                <div className="h-full bg-[#e8594f] transition-all duration-500 ease-out" style={{
              width: `${step / 7 * 100}%`
            }} data-id="element-2116" data-mp="3aa2t6zn9jrsx" />
              </div>

              {/* Progress Dots */}
              <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-0" data-id="element-2117" data-mp="1umo0i2rcwh28">
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 1 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-2118" data-mp="1zmaewfix83cm" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 6 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-2119" data-mp="29d53vrnjxh1r" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step > 6 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-2120" data-mp="2ieuuwwdiudz0" />
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-4" data-id="element-2121" data-mp="o0tw2bhz1qyx">
              Step {showMilitaryStep || step <= 4 ? step : step - 1} of{' '}
              {showMilitaryStep ? 6 : 5}
            </p>
          </div>

          {/* Back Button */}
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 md:mb-6 transition-colors" data-id="element-2122" data-mp="kzqtsooff3i6">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2123" data-mp="ux56qeb55a5q" />
            <span className="font-medium" data-id="element-2124" data-mp="uxy5ot3uyvdb">Back</span>
          </button>

          {/* Form Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-8 md:p-12" data-id="element-2125" data-mp="pxah0e33qqm9">
            {/* Step 1: Highest Education */}
            {step === 1 && <div className="max-w-xl mx-auto" data-id="element-2126" data-mp="zwhquramo34m">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2127" data-mp="zvq66bikjpt4">
                  What is your highest level of education?
                </h2>
                <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2128" data-mp="18p3asapzwfv1">
                  <option value="" data-id="element-2129" data-mp="269359kyuj0b2">-- Please Select --</option>
                  <option value="high-school" data-id="element-2130" data-mp="1sabti57ghqq3">High School Diploma/GED</option>
                  <option value="some-college" data-id="element-2131" data-mp="1scfuofdz0bmo">Some College</option>
                  <option value="associates" data-id="element-2132" data-mp="1x84yww0xqgpb">Associate's Degree</option>
                  <option value="bachelors" data-id="element-2133" data-mp="1ik0dhkahzr2u">Bachelor's Degree</option>
                  <option value="masters" data-id="element-2134" data-mp="1iip9pkh1yjbq">Master's Degree</option>
                  <option value="doctorate" data-id="element-2135" data-mp="1ncthg5y56vdv">Doctorate</option>
                  <option value="some-high-school" data-id="element-2136" data-mp="19kdltq7klzmz">Some High School</option>
                </select>
              </div>}

            {/* Step 2: HS Graduation Year */}
            {step === 2 && <div className="max-w-xl mx-auto" data-id="element-2137" data-mp="zlo7g7evuxbh">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2138" data-mp="zm7n1hh299mc">
                  When did you graduate high school?
                </h2>
                <select value={formData.hsGraduationYear} onChange={e => updateField('hsGraduationYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2139" data-mp="27ud7irq1o577">
                  <option value="" data-id="element-2140" data-mp="1o876j51e3ye0">-- Please Select --</option>
                  {years.map(year => <option key={year} value={year} data-id="element-2141" data-mp="1k6c4tzfjrizp">
                      {year}
                    </option>)}
                  <option value="before-1965" data-id="element-2142" data-mp="1f6hepy3lixfp">Before 1965</option>
                </select>
              </div>}

            {/* Step 3: Start Date */}
            {step === 3 && <div className="max-w-xl mx-auto" data-id="element-2143" data-mp="1aa0most9zyxc">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2144" data-mp="39la4vhlpmw09">
                  When would you like to start classes?
                </h2>
                <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2145" data-mp="3c687xa34r2bu">
                  <option value="" data-id="element-2146" data-mp="3qw763cuoqdrb">-- Please Select --</option>
                  <option value="immediately" data-id="element-2147" data-mp="2t85bwbc6054j">Immediately</option>
                  <option value="1-3-months" data-id="element-2148" data-mp="2y4vr6wg5awow">1-3 months</option>
                  <option value="3-6-months" data-id="element-2149" data-mp="2y7i99q0nwuld">3-6 months</option>
                  <option value="6-12-months" data-id="element-2150" data-mp="33371176fx774">6-12 months</option>
                  <option value="1-year-plus" data-id="element-2151" data-mp="384eb5cw22igy">1+ year</option>
                  <option value="undecided" data-id="element-2152" data-mp="381iavhwqjhpc">Undecided</option>
                </select>
              </div>}

            {/* Step 4: Military Affiliation (hidden in the v1 template,
                which posts "None" without asking) */}
            {step === 4 && showMilitaryStep && <div className="max-w-xl mx-auto" data-id="element-2153" data-mp="2jfha2ejqlor1">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2154" data-mp="2ehxth7zkgzvx">
                  Are you affiliated with the U.S. military?
                </h2>
                <select value={formData.militaryAffiliation} onChange={e => updateField('militaryAffiliation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2155" data-mp="2ofvdtkeb2cj9">
                  <option value="" data-id="element-2156" data-mp="29kfjrfdq6gmv">-- Please Select --</option>
                  <option value="none" data-id="element-2157" data-mp="24r2wp9op2hw8">None</option>
                  <option value="active-duty" data-id="element-2158" data-mp="1h21s5xizfqw8">Active Duty</option>
                  <option value="veteran" data-id="element-2159" data-mp="1h0gva1973vv7">Veteran</option>
                  <option value="guard" data-id="element-2160" data-mp="1d1htgpirgf7z">Guard</option>
                  <option value="spouse" data-id="element-2161" data-mp="1cyvomxn98dtg">Spouse</option>
                  <option value="dependent" data-id="element-2162" data-mp="12vztl26af1ab">Dependent</option>
                  <option value="unknown" data-id="element-2163" data-mp="17n84k2n9mupl">Unknown</option>
                </select>
              </div>}

            {/* Step 5: Address (was step 4) */}
            {step === 5 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-2164" data-mp="t0eupcwimh4s">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2165" data-mp="xyq81xm0r9rr">
                  Where are you located?
                </h2>

                <div data-id="element-2166" data-mp="1qmvtkmkwd2sn">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2167" data-mp="1qlb070abjpwd">
                    Street Address <span className="text-red-500" data-id="element-2168" data-mp="1bo1tmzcg2fk2">*</span>
                  </label>
                  <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 transition-colors" data-id="element-2169" data-mp="1gndbycsglt3r" />
                </div>

                {formData.city && formData.state && formData.zipCode ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-2170" data-mp="3dnqa13tmd6bd">
                    <span className="text-green-600 text-xl" data-id="element-2171" data-mp="38qggbbz768hq">📍</span>
                    <div className="flex-1" data-id="element-2172" data-mp="34olgyd0tu56l">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-2173" data-mp="34pwg1zjbuord">
                        {formData.city}, {formData.state} {formData.zipCode}
                      </p>
                    </div>
                    <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                city: '',
                state: '',
                zipCode: ''
              }));
            }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-2174" data-mp="an59cnmxp82">
                      change
                    </button>
                  </div> : <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-2175" data-mp="j7xvk7rah8bd">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2176" data-mp="21wixibe4djfx">
                      <span className="text-yellow-600 text-xl" data-id="element-2177" data-mp="26uamawjgwpfw">📍</span>
                      <p className="text-sm font-semibold text-gray-700" data-id="element-2178" data-mp="26woij8pvw7p1">
                        Enter your ZIP Code
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2179" data-mp="2fw1qty29uq86">
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateField('zipCode', val);
                if (val.length === 5) {
                  lookupZipCode(val);
                }
              }} placeholder="e.g. 90210" maxLength={5} className="flex-1 px-3 py-2 border border-yellow-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white" data-id="element-2180" data-mp="2kuv6nx516tbi" />
                      {formData.zipCode.length === 5 && !formData.city && <p className="text-xs text-red-500" data-id="element-2181" data-mp="cpxs66hpy3n3">
                          ZIP not found. Try again.
                        </p>}
                    </div>
                  </div>}
              </div>}

            {/* Step 6: Contact Info (was step 5) */}
            {step === 6 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-2182" data-mp="3r2ob125d5my">
                <div className="text-center mb-6 md:mb-8" data-id="element-2183" data-mp="8p4339sjiism">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-2184" data-mp="wdnfo1imhhqi">
                    Last step! Where should we send your matches?
                  </h2>
                  <p className="text-gray-600" data-id="element-2185" data-mp="10h362g43mlur">
                    We'll match you with top programs in {formData.city},{' '}
                    {formData.state}.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2186" data-mp="1fcj7x7ax7m1p">
                  <div data-id="element-2187" data-mp="1fayclfgs7z2s">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2188" data-mp="1faojhj0fxbsv">
                      First Name <span className="text-red-500" data-id="element-2189" data-mp="1k52j5xm4y53c">*</span>
                    </label>
                    <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} placeholder="John" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2190" data-mp="1tg8awmoxq2s3" />
                  </div>
                  <div data-id="element-2191" data-mp="1ainqpsx47sdt">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2192" data-mp="1ajysjn702q2a">
                      Last Name <span className="text-red-500" data-id="element-2193" data-mp="16ix0prfexgac">*</span>
                    </label>
                    <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} placeholder="Doe" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2194" data-mp="1kieihmuajta7" />
                  </div>
                </div>

                <div data-id="element-2195" data-mp="2yyerpyjz7qml">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2196" data-mp="2yywiwgrs7enn">
                    Email Address <span className="text-red-500" data-id="element-2197" data-mp="32yymwwkzok63">*</span>
                  </label>
                  <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="john@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2198" data-mp="37yb9rhbv1r8o" />
                </div>

                <div data-id="element-2199" data-mp="2k9iabaw1519b">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2200" data-mp="2k6w1z91behq1">
                    Phone Number <span className="text-red-500" data-id="element-2201" data-mp="2p6iefg9bjtxs">*</span>
                  </label>
                  <input type="tel" value={formData.phone} onChange={handlePhoneChange} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2202" data-mp="2u14tpuiiu6n5" />
                </div>

                <div className="pt-4" data-id="element-2203" data-mp="1p059s9riuva5">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-2204" data-mp="1p1gbm41epsym">
                      <div className="w-5 h-5 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-label="Loading consent terms" data-id="element-2205" data-mp="1k00rce71zqha" />
                      <span data-id="element-2206" data-mp="1f4br08n7m5jx">Loading consent terms…</span>
                    </div> : tcpaError ? <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-2207" data-mp="1b1otbr3uxqet">
                      {tcpaError}
                    </div> : tcpaResult?.tcpaDisclaimer ? <label className="flex items-start gap-3 cursor-pointer" data-id="element-2208" data-mp="11bc95zzyfz97">
                      <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f]" data-id="element-2209" data-mp="wc93cdc5hp06" />
                      <span className="text-xs text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                __html: sanitizeTcpaHtml(tcpaResult.tcpaDisclaimer)
              }} data-id="element-2210" data-mp="nc2td1fnv75r" />
                    </label> : null}
                </div>
              </div>}

            {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg max-w-xl mx-auto" data-id="element-2211" data-mp="139ne6ml8y35w">
                <p className="text-red-600 text-sm" data-id="element-2212" data-mp="17a7chthk3ctm">{error}</p>
              </div>}

            <div className="mt-8 max-w-xl mx-auto" data-id="element-2213" data-mp="tb7lwgahgnut">
              <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-2214" data-mp="t9d0z1z7ovqq">
                {step === 6 ? 'See Matching Colleges 🎓' : 'Continue →'}
              </button>

              {step === 6 && <p className="text-center text-gray-700 mt-4" style={{
            fontSize: '12.5px',
            lineHeight: '1.5'
          }} data-id="element-2215" data-mp="ocd33ecimzap">
                  By clicking "See Matching Colleges," you expressly consent to
                  receive autodialed and/or pre-recorded marketing text messages
                  (SMS/MMS) from FindOnlineDegrees.org at the number provided.
                  Consent is not a condition of any purchase. Msg &amp; data
                  rates may apply. Reply STOP to opt out. Consent is also given
                  for newsletters and email offers powered by Arcamax LLC. View
                  our{' '}
                  <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-2216" data-mp="qq9mcxs9uznz">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-2217" data-mp="zr7uns9qttcd">
                    Terms &amp; Conditions
                  </a>
                  .
                </p>}

              {step !== 6 && <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800" data-id="element-2218" data-mp="31i1bsjzdqimh">
                  <span className="text-blue-500 text-lg leading-none mt-0.5" data-id="element-2219" data-mp="2wkrob9vjwh1a">
                    ℹ️
                  </span>
                  <span data-id="element-2220" data-mp="10ifzcj0da43l">
                    {step === 0 && 'Selecting your degree level and area of study helps us match you with the most relevant accredited programs.'}
                    {step === 1 && 'Your education background helps schools understand where you are in your academic journey and recommend the right program level.'}
                    {step === 2 && 'Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline.'}
                    {step === 3 && 'Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule.'}
                    {step === 4 && 'Military affiliation may qualify you for special benefits, scholarships, and program options designed for service members and their families.'}
                    {step === 5 && 'Your location helps us find programs available in your area and determine eligibility for state-specific financial aid.'}
                  </span>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}